import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useClickableTooltip } from '../hooks/useClickableTooltip';

interface BaseTooltipProps {
  triggerRef: React.RefObject<HTMLElement>;
  children: React.ReactNode | ((closeTooltip: () => void) => React.ReactNode);
  className?: string;
}

const BaseTooltip: React.FC<BaseTooltipProps> = ({ triggerRef, children, className = '' }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { isVisible, closeTooltip } = useClickableTooltip({
    triggerRef,
    tooltipRef
  });
  const [position, setPosition] = useState<{ bottom: number; left: number } | null>(null);

  // Cache for performance optimization
  const scrollableContainersRef = useRef<HTMLElement[]>([]);
  const lastCalculationRef = useRef<{ position: { bottom: number; left: number }; timestamp: number } | null>(null);

  // Memoized function to find scrollable containers (only recalculates when trigger changes)
  const findScrollableContainers = useCallback((element: HTMLElement): HTMLElement[] => {
    const containers: HTMLElement[] = [];
    let current = element.parentElement;

    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const hasScroll =
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowX === 'auto' ||
        style.overflowX === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll';

      if (hasScroll || current.scrollWidth > current.clientWidth || current.scrollHeight > current.clientHeight) {
        containers.push(current);
      }
      current = current.parentElement;
    }

    return containers;
  }, []);

  const checkTriggerInViewport = useCallback(() => {
    const el = triggerRef.current;
    if (!el) {
      return false;
    }
    const { right, left, top, bottom } = el.getBoundingClientRect();

    return right > 0 && left < window.innerWidth && bottom > 0 && top < window.innerHeight;
  }, [triggerRef]);

  // Calculate position with caching for performance
  const calculatePosition = useCallback(() => {
    const now = performance.now();

    // Cache result for 8ms to avoid excessive calculations during rapid events
    if (lastCalculationRef.current && now - lastCalculationRef.current.timestamp < 8) {
      return lastCalculationRef.current.position;
    }

    const triggerElement = triggerRef.current;
    if (!triggerElement) {
      return { bottom: 0, left: 0 };
    }

    const rect = triggerElement.getBoundingClientRect();

    const tooltipHalfWidth = 128;
    // Reserve space for the customizer menu sidebar
    const rightMargin = 72;
    const visibleLeft = Math.max(0, rect.left);
    const visibleRight = Math.min(window.innerWidth - rightMargin, rect.right);
    const rawCenter = visibleRight > visibleLeft ? (visibleLeft + visibleRight) / 2 : rect.left + rect.width / 2; // fallback if fully off-screen
    const centerLeft = Math.min(
      Math.max(tooltipHalfWidth, rawCenter),
      window.innerWidth - rightMargin - tooltipHalfWidth
    );

    const newPosition = {
      bottom: window.innerHeight - rect.top, // Position above the trigger
      left: centerLeft
    };

    // Cache the result
    lastCalculationRef.current = {
      position: newPosition,
      timestamp: now
    };

    return newPosition;
  }, [triggerRef]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      if (!checkTriggerInViewport()) {
        closeTooltip();
        return;
      }
      setPosition(calculatePosition());
    }
  }, [isVisible, calculatePosition, checkTriggerInViewport, closeTooltip]);

  // Update position on scroll and zoom events with performance optimizations
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const triggerElement = triggerRef.current;
    if (!triggerElement) {
      return;
    }

    // Use cached containers or find them once
    if (scrollableContainersRef.current.length === 0) {
      scrollableContainersRef.current = findScrollableContainers(triggerElement);
    }

    // Use requestAnimationFrame for smooth updates and better performance
    let rafId: number | null = null;
    // Flag to suppress scroll events triggered by a zoom wheel gesture
    let isZooming = false;
    let zoomClearTimerId: ReturnType<typeof setTimeout> | null = null;

    // Scroll closes the tooltip unless it was triggered by a zoom wheel gesture
    const handleScroll = () => {
      if (isZooming) {
        return;
      }
      closeTooltip();
    };

    const updatePositionOrClose = () => {
      if (rafId) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        if (!checkTriggerInViewport()) {
          closeTooltip();
        } else {
          setPosition(calculatePosition());
        }
        rafId = null;
      });
    };

    // Use a single optimized wheel handler
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        // Mark that a zoom gesture is in progress so the resulting scroll event is ignored
        isZooming = true;
        if (zoomClearTimerId) {
          clearTimeout(zoomClearTimerId);
        }
        zoomClearTimerId = setTimeout(() => {
          isZooming = false;
        }, 150);
        updatePositionOrClose();
      }
    };

    // Add listeners with optimized approach
    const containers = scrollableContainersRef.current;

    containers.forEach(container => {
      container.addEventListener('scroll', handleScroll, { passive: true });
      container.addEventListener('wheel', handleWheel, { passive: true });
    });

    // Global listeners for window events
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updatePositionOrClose, { passive: true });

    return () => {
      // Cleanup
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (zoomClearTimerId) {
        clearTimeout(zoomClearTimerId);
      }

      containers.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', handleWheel);
      });

      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updatePositionOrClose);
    };
  }, [isVisible, calculatePosition, checkTriggerInViewport, closeTooltip, triggerRef, findScrollableContainers]);

  // Reset caches and position when tooltip closes for memory efficiency
  useEffect(() => {
    if (!isVisible) {
      scrollableContainersRef.current = [];
      lastCalculationRef.current = null;
      setPosition(null);
    }
  }, [isVisible]);

  if (!isVisible || !position) {
    return null;
  }

  return (
    <div
      ref={tooltipRef}
      data-tooltip-overlay
      className={`fixed z-[99999] w-64 cursor-default rounded-xl border border-gray-200 bg-white shadow-lg ${className}`}
      style={{
        bottom: `${position.bottom}px`,
        left: `${position.left}px`,
        transform: 'translateX(-50%)'
      }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      {/* Arrow pointing down */}
      <div className="absolute -bottom-1 left-1/2 size-1.5 -translate-x-1/2 rotate-45 border-b border-r border-gray-200 bg-white"></div>

      <div className="p-3">{typeof children === 'function' ? children(closeTooltip) : children}</div>
    </div>
  );
};

export default BaseTooltip;
